import BuilderAppCol, {
    BuilderAppColProps,
} from '@form-engine/builder-application/components/app-col';
import Drawer from '@form-engine/builder-application/components/drawer';
import BuilderDrawerTabs from '@form-engine/builder-application/components/drawer-tabs';
import BuilderSpinnerBoundary from '@form-engine/builder-application/components/spinner-boundary';
import { useFormEntity } from '@form-engine/builder-application/hooks/use-form-api';
import { useSessionLogEntity } from '@form-engine/builder-application/hooks/use-session-log-api';
import {
    FormProvider,
    useFormElements,
} from '@form-engine/core-application/hooks/use-form';
import {
    FormSessionProvider,
    useFormSessionValues,
} from '@form-engine/core-application/hooks/use-formsession';
import { LogDefinition } from '@form-engine/core-application/types/form';
import React, { useMemo } from 'react';
import { Nav, Table } from 'react-bootstrap';
import { ArrowDownCircle, XLg } from 'react-bootstrap-icons';
import { Link, To, useParams } from 'react-router-dom';
import { i18n } from '@form-engine/builder-application';
import { exportLogsAsFile } from '.';
import FormEngine from '@form-engine/core-application';
import { FormEngineStateProvider } from '@form-engine/core-application/components/engine';
import { BuilderSessionTree } from '@form-engine/builder-form-editor/components/session-drawer';
import { FormEditorProvider } from '@form-engine/builder-form-editor/hooks/use-form-editor';

function BuilderFormSessionDataList({
    children,
}: {
    children: React.ReactNode;
}) {
    const { state: elements = [] } = useFormElements();
    const inputElements = useMemo(() => {
        const fields = FormEngine.getFieldElements();
        return elements.filter((element) => element.type in fields);
    }, [elements]);
    const values = useFormSessionValues();

    return (
        <Table striped className="mb-0 text-xs">
            <tbody>
                {children}
                {inputElements.map((element) => {
                    return (
                        <tr key={element.id}>
                            <th>
                                {((element?.label ||
                                    element?.name) as React.ReactNode) ?? ''}
                            </th>
                            <td>
                                {element?.name &&
                                    (typeof values[element.name] === 'string'
                                        ? (values[element.name] as string)
                                        : JSON.stringify(
                                              values[element.name],
                                              null,
                                              2
                                          ))}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </Table>
    );
}

function BuilderSessionLogFormValues({
    log = {},
    form = {},
}: {
    log?: LogDefinition | null;
    form?: FormDefinition | null;
}) {
    const info = useMemo(() => {
        try {
            return JSON.parse(log?.value ?? 'null');
        } catch {
            return null;
        }
    }, [log?.value]);

    if (!form) return null;

    return (
        <div className="feb-view m-0">
            <div className="feb-view-inner p-0">
                <Drawer>
                    <FormProvider value={form}>
                        <FormSessionProvider value={info?.session || {}}>
                            <BuilderFormSessionDataList>
                                <tr>
                                    <th>Form</th>
                                    <td>{form?.name}</td>
                                </tr>
                                <tr>
                                    <th>Workflow ID</th>
                                    <td>{info?.definition?.name}</td>
                                </tr>
                            </BuilderFormSessionDataList>
                        </FormSessionProvider>
                    </FormProvider>
                </Drawer>
            </div>
        </div>
    );
}

function BuilderSessionLogScope({
    log = {},
    form = {},
}: {
    log?: LogDefinition | null;
    form?: FormDefinition | null;
}) {
    const info = useMemo(() => {
        try {
            return JSON.parse(log?.value ?? 'null');
        } catch {
            return null;
        }
    }, [log?.value]);

    const workflow = useMemo(() => ({}), []);
    const editor = useMemo(() => ({}), []);

    if (!form) return null;

    return (
        <FormEditorProvider value={editor}>
            <FormEngineStateProvider
                form={form}
                session={info.session}
                workflow={workflow}
            >
                <BuilderSessionTree />
            </FormEngineStateProvider>
        </FormEditorProvider>
    );
}

export default function BuilderSessionLogDetailView({
    as: Component = BuilderAppCol,
    closePath,
    ...props
}: BuilderAppColProps & { closePath: To }) {
    const { id } = useParams();
    const { state: log = {}, loading } = useSessionLogEntity(id);
    const { state: form = {}, loading: loadingForm } = useFormEntity(
        log?.formId
    );
    const [view, setView] = React.useState('formValues');

    return (
        <Component md={4} border="left" {...props}>
            <BuilderDrawerTabs
                items={{
                    formValues: 'Form Values',
                    session: i18n.c('session-logs.item.session-info'),
                }}
                activeKey={view}
                onSelect={(key: string) => setView(key)}
            >
                <Nav.Item className="ms-auto">
                    <Nav.Link
                        as={'button'}
                        onClick={() =>
                            log && exportLogsAsFile([log], `${log.id}.json`)
                        }
                        title={i18n.c('session-logs.item.export')}
                        className="pe-1"
                    >
                        <ArrowDownCircle />
                    </Nav.Link>
                </Nav.Item>

                {closePath && (
                    <Nav.Item>
                        <Nav.Link as={Link} to={closePath} className="ps-1">
                            <XLg />
                        </Nav.Link>
                    </Nav.Item>
                )}
            </BuilderDrawerTabs>

            <BuilderSpinnerBoundary show={loading || loadingForm} bar={false}>
                {view === 'formValues' && (
                    <BuilderSessionLogFormValues log={log} form={form} />
                )}
                {view === 'session' && (
                    <BuilderSessionLogScope log={log} form={form} />
                )}
            </BuilderSpinnerBoundary>
        </Component>
    );
}
